import * as React from 'react';
import { useState, useRef, useCallback } from 'react';
import { FaTimes, FaTasks } from 'react-icons/fa';
import CrmTaskService, { CrmTask } from '../../sales/services/CrmTaskService';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface EditShiftData {
  shiftId: number | null;
  startDate: string;
  startTime: string;
  endTime: string;
  employeesNeeded: number;
  workArea: string;
  notes: string;
  scope: string;
  crm_task: number | null;
  crm_task_title: string;
}

interface EditShiftModalProps {
  open: boolean;
  onClose: () => void;
  editData: EditShiftData;
  onEditDataChange: (data: EditShiftData) => void;
  onSave: () => void;
  onDelete: () => void;
  workAreaOptions: any[];
  addOneHour: (time: string) => string;
}

function EditShiftModal({
  open,
  onClose,
  editData,
  onEditDataChange,
  onSave,
  onDelete,
  workAreaOptions,
  addOneHour,
}: EditShiftModalProps) {
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskOptions, setTaskOptions] = useState<CrmTask[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const taskSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (open) {
      setTaskSearchTerm('');
      setTaskOptions([]);
      setShowTaskDropdown(false);
    }
  }, [open]);

  const handleTaskSearch = useCallback(async (searchValue: string) => {
    if (taskSearchTimeoutRef.current) {
      clearTimeout(taskSearchTimeoutRef.current);
    }
    setTaskSearchTerm(searchValue);
    if (!searchValue || searchValue.length < 2) {
      setTaskOptions([]);
      setShowTaskDropdown(false);
      return;
    }
    taskSearchTimeoutRef.current = setTimeout(async () => {
      setTaskLoading(true);
      try {
        const tasks = await CrmTaskService.list({
          search: searchValue,
          status: 'OPEN,IN_PROGRESS',
          page_size: 15
        });
        setTaskOptions(tasks);
        setShowTaskDropdown(true);
      } catch (error) {
        console.error('Task search error:', error);
        setTaskOptions([]);
      } finally {
        setTaskLoading(false);
      }
    }, 300);
  }, []);

  const formatTaskLabel = (task: CrmTask): string => {
    const dueDate = task.due_date
      ? new Date(task.due_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
      : '';
    const partnerName = task.partner_name || '';
    return `${dueDate ? `[${dueDate}] ` : ''}${task.title}${partnerName ? ` – ${partnerName}` : ''}`;
  };

  if (!open) return null;

  const labelStyle = {
    ...ps.labelStyle,
    marginBottom: 0,
  };
  const inputStyle = {
    ...ps.inputStyle,
    marginTop: '2px',
    boxSizing: 'border-box',
  };
  const editFieldWrapper: React.CSSProperties = {
    ...ps.fieldWrapper,
    padding: '4px 10px',
    borderRadius: 8,
  };

  const handleClose = () => {
    setTaskSearchTerm('');
    setTaskOptions([]);
    setShowTaskDropdown(false);
    onClose();
  };

  return (
    <div
      className="glass-modal-backdrop"
      style={{
        position:'fixed', top:0,left:0,right:0,bottom:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        zIndex: 5001
      }}
    >
      <div
        className="glass-modal"
        style={{
          width:'520px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding:'20px 24px 16px 24px',
          fontSize:'1rem',
          fontFamily: 'Inter, Arial, sans-serif',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
        data-testid="edit-shift-div"
      >
        <h2 style={{
          margin:0,
          fontSize:'1.18rem',
          fontWeight:700,
          color: colors.navy,
          letterSpacing: '0.5px',
        }}>
          Edit Shift
        </h2>
        <p style={{ margin: 0, color: colors.textLight, fontSize: '0.97rem' }}>Shift #{editData.shiftId}</p>

        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {/* Start Date */}
          <div className="pa-field" style={editFieldWrapper}>
            <label style={labelStyle}>Start Date:</label>
            <input
              type="date"
              value={editData.startDate}
              onChange={e => onEditDataChange({ ...editData, startDate: e.target.value })}
              style={inputStyle as React.CSSProperties}
            />
          </div>
          {/* Start/End Time */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="pa-field" style={{ ...editFieldWrapper, flex: 1 }}>
              <label style={labelStyle}>Start Time:</label>
              <input
                type="time"
                value={editData.startTime}
                onChange={e => {
                  const v = e.target.value;
                  const update: any = { ...editData, startTime: v };
                  if (v) update.endTime = addOneHour(v);
                  onEditDataChange(update);
                }}
                style={inputStyle as React.CSSProperties}
                data-testid="shift-start-time"
              />
            </div>
            <div className="pa-field" style={{ ...editFieldWrapper, flex: 1 }}>
              <label style={labelStyle}>End Time:</label>
              <input
                type="time"
                value={editData.endTime}
                onChange={e => onEditDataChange({ ...editData, endTime: e.target.value })}
                style={inputStyle as React.CSSProperties}
                data-testid="shift-end-time"
              />
            </div>
          </div>
          {/* Employees Needed & Work Area */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div className="pa-field" style={{ ...editFieldWrapper, flex: 1 }}>
              <label style={labelStyle}>Employees Needed:</label>
              <input
                type="number"
                min={1}
                value={editData.employeesNeeded}
                onChange={e => onEditDataChange({ ...editData, employeesNeeded: Number(e.target.value) })}
                style={inputStyle as React.CSSProperties}
              />
            </div>
            <div className="pa-field" style={{ ...editFieldWrapper, flex: 1 }}>
              <label style={labelStyle}>Work Area:</label>
              <select
                value={editData.workArea}
                onChange={e => onEditDataChange({ ...editData, workArea: e.target.value })}
                style={inputStyle as React.CSSProperties}
              >
                {workAreaOptions.map(wa => (
                  <option key={wa.id} value={wa.id}>{wa.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Catering Notes */}
          <div className="pa-field" style={editFieldWrapper}>
            <label style={labelStyle}>Notes:</label>
            <textarea
              value={editData.notes}
              onChange={(e) =>
                onEditDataChange({ ...editData, notes: e.target.value })
              }
              placeholder="Important notes for service chef, production and staff..."
              style={{
                ...inputStyle,
                minHeight: '80px',
                resize: 'vertical',
                fontFamily: 'inherit'
              } as React.CSSProperties}
            />
          </div>

          {/* Related Task */}
          <div className="pa-field" style={{ ...editFieldWrapper, position: 'relative' }}>
            <label style={labelStyle}>
              <FaTasks style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Related Task (optional):
            </label>
            {editData.crm_task ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: `rgba(82, 113, 165, 0.1)`,
                borderRadius: 7,
                border: `1px solid rgba(82, 113, 165, 0.3)`
              }}>
                <FaTasks style={{ color: colors.navy, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.95rem', color: colors.navy }}>
                  {editData.crm_task_title}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onEditDataChange({ ...editData, crm_task: null, crm_task_title: '' });
                    setTaskSearchTerm('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: colors.textMuted
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={taskSearchTerm}
                  onChange={(e) => handleTaskSearch(e.target.value)}
                  onFocus={() => taskOptions.length > 0 && setShowTaskDropdown(true)}
                  placeholder="Search for task..."
                  style={inputStyle as React.CSSProperties}
                />
                {taskLoading && (
                  <div style={{ position: 'absolute', right: 12, top: 38, color: colors.textLight }}>
                    ...
                  </div>
                )}
                {showTaskDropdown && taskOptions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    boxShadow: shadows.medium,
                    maxHeight: 200,
                    overflowY: 'auto',
                    zIndex: 10
                  }}>
                    {taskOptions.map(task => (
                      <div
                        key={task.id}
                        onClick={() => {
                          onEditDataChange({
                            ...editData,
                            crm_task: task.id,
                            crm_task_title: formatTaskLabel(task)
                          });
                          setShowTaskDropdown(false);
                          setTaskSearchTerm('');
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.borderLight}`,
                          fontSize: '0.92rem'
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = colors.bgMuted)}
                        onMouseOut={(e) => (e.currentTarget.style.background = colors.bgCard)}
                      >
                        {formatTaskLabel(task)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Scope */}
          <div className="pa-field" style={editFieldWrapper}>
            <label style={labelStyle}>Apply to:</label>
            <select
              value={editData.scope}
              onChange={e => onEditDataChange({ ...editData, scope: e.target.value })}
              style={inputStyle as React.CSSProperties}
            >
              <option value="this">Only this shift</option>
              <option value="future">This + future</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop:'10px', textAlign:'right', display:'flex', gap:'10px', justifyContent:'flex-end' }}>
          <button
            onClick={onSave}
            style={{
              ...ps.buttonPrimary,
              padding:'8px 18px',
              fontSize:'1rem',
            }}
            data-testid="save-edit-shift-button"
          >
            Save
          </button>
          <button
            onClick={onDelete}
            style={{
              backgroundColor: colors.dangerLight, color: colors.danger,
              border:'none', borderRadius: 9999,
              padding:'8px 18px',
              fontSize:'1rem',
              fontWeight: 500,
              cursor:'pointer',
              boxShadow: shadows.soft,
              transition: 'background 0.18s'
            }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = '#ffd6d6'}
            onMouseOut={e => e.currentTarget.style.backgroundColor = colors.dangerLight}
            data-testid="delete-shift-button"
          >
            Delete
          </button>
          <button
            onClick={handleClose}
            style={{
              ...ps.buttonSecondary,
              padding:'8px 18px',
              fontSize:'1rem',
            }}
            data-testid="cancel-edit-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditShiftModal;
